import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';

interface Division {
  name: string;
  title: string;
}

export const DivisionPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<Division[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        setLoading(true);
        setError(null);

        const { items: divisionsResult } = await catalogApi.getEntities({
          filter: { kind: 'Group', 'spec.type': 'division' },
          fields: ['metadata.name', 'metadata.title'],
        });

        const divList: Division[] = divisionsResult
          .map((d: any) => ({
            name: d.metadata.name,
            title: d.metadata.title || d.metadata.name,
          }))
          .sort((a: Division, b: Division) => a.title.localeCompare(b.title));

        setDivisions(divList);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load divisions from catalog',
        );
        setLoading(false);
      }
    };

    fetchEntities();
  }, [catalogApi]);

  const handleChange = (event: any) => {
    onChange(event.target.value || '');
  };

  if (loading) {
    return (
      <FormControl fullWidth margin="normal" disabled>
        <InputLabel id="division-select-label">Division</InputLabel>
        <Select labelId="division-select-label" disabled>
          <MenuItem disabled>
            <CircularProgress size={16} style={{ marginRight: 8 }} /> Loading...
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel id="division-select-label">Division</InputLabel>
        <Select labelId="division-select-label" disabled>
          <MenuItem disabled>Error loading divisions</MenuItem>
        </Select>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0}
    >
      <InputLabel id="division-select-label">Division</InputLabel>
      <Select
        labelId="division-select-label"
        id="division-select"
        value={formData || ''}
        onChange={handleChange}
      >
        <MenuItem value="">
          <em>Select a division...</em>
        </MenuItem>
        {divisions.map(div => (
          <MenuItem key={div.name} value={div.name}>
            {div.title}
          </MenuItem>
        ))}
      </Select>
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>
          {rawErrors.map((err: any) => err.stack).join(', ')}
        </FormHelperText>
      )}
    </FormControl>
  );
};
